import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Coins, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PackCard, PackRow } from "@/components/pricing/PackCard";
import { useAuth } from "@/contexts/useAuth";
import { api } from "@/lib/api";
import type { ApiResponse } from "@/lib/api";
import { scrollToSettled } from "@/lib/scroll-settle";
import { isHistoryRestore } from "@/lib/scroll-memory";
import { CREDITS_ANCHOR, isCreditsVisit, useCreditCheckout } from "@/hooks/useCreditCheckout";
import { Link } from "react-router-dom";
import { CheckoutStrip } from "@/components/trust/CheckoutStrip";

// THE CREDIT PACKS ON THE LANDING PAGE (#credits). Where "Buy credits" in the
// Telegram studio and bot lands, and where a website visitor buys too: one
// balance, spent by every tool. The token exchange, the safety rules and the
// resume after sign-up live in hooks/useCreditCheckout.ts; this draws it.
//
// A Telegram visit signed in as a studio account (no email of its own) can
// also, optionally, add an email and password (register), or sign in to the
// website account it already has (log in), which folds the studio account
// into it and moves the Telegram id there -- once that Telegram account
// confirms it in the bot: the password proves the website account, and only
// Telegram itself can prove the Telegram one (routes/user.js /merge-studio).

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Merged = { email: string; credits: number };

function RegisterForm({ onSaved, onExists }: { onSaved: (email: string) => void; onExists: (email: string) => void }) {
  const { t } = useTranslation();
  const id = useId();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "exists" | "invalid" | "failed">("idle");

  async function save(e: FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!EMAIL_RE.test(address) || password.length < 6) {
      setState("invalid");
      return;
    }
    setState("saving");
    const r = await api.addEmail(address, password);
    if (r.success) {
      onSaved(r.data?.email || address);
      return;
    }
    if (r.error === "email_exists") {
      setState("exists");
      onExists(address);
      return;
    }
    setState(r.error === "invalid_email" || r.error === "password_too_short" ? "invalid" : "failed");
  }

  return (
    <form onSubmit={save} data-testid="register-form" className="mt-3 space-y-3">
      <p className="text-muted-foreground">{t("landingCredits.register.body")}</p>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-email`}>{t("landingCredits.email")}</Label>
        <Input id={`${id}-email`} type="email" autoComplete="email" value={email}
          onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-password`}>{t("landingCredits.newPassword")}</Label>
        <Input id={`${id}-password`} type="password" autoComplete="new-password" value={password}
          onChange={(e) => setPassword(e.target.value)} />
      </div>
      <Button type="submit" className="w-full sm:w-auto" disabled={state === "saving"}>
        {state === "saving" ? t("landingCredits.saving") : t("landingCredits.register.submit")}
      </Button>
      {state === "exists" && <p className="text-destructive">{t("landingCredits.register.exists")}</p>}
      {state === "invalid" && <p className="text-destructive">{t("landingCredits.register.invalid")}</p>}
      {state === "failed" && <p className="text-destructive">{t("landingCredits.register.failed")}</p>}
    </form>
  );
}

const MERGE_ERRORS: Record<string, string> = {
  invalid_credentials: "landingCredits.login.invalid",
  credentials_required: "landingCredits.login.invalid",
  not_website_account: "landingCredits.login.notWebsite",
  fold_refused: "landingCredits.login.refused",
  telegram_already_linked: "landingCredits.login.linkedElsewhere",
  not_studio_account: "landingCredits.login.notStudio",
  wrong_telegram: "landingCredits.login.wrongTelegram",
  stale: "landingCredits.login.stale",
  merge_expired: "landingCredits.login.expired",
};

type Failure = { key: string; values?: Record<string, unknown> };

/**
 * Why a join was refused, in the visitor's language. A fold refusal says the
 * one step that unblocks it (foldRefusal's reason and figures), as the
 * studio's own link screen does.
 */
function mergeFailure(r: ApiResponse<unknown>): Failure {
  const body = r.body || {};
  const code = String(body.error || r.error || "");
  if (code === "fold_refused") {
    const reason = String(body.reason || "");
    if (reason === "avatar_limit_after_link") {
      return { key: "landingCredits.login.refusedAvatars",
        values: { total: body.avatars, limit: body.limit, extra: body.extra } };
    }
    if (reason === "first_avatar_used") return { key: "landingCredits.login.refusedFirstAvatar" };
  }
  return { key: MERGE_ERRORS[code] || "landingCredits.login.failed" };
}

type Pending = { url: string; poll: string; until: number; username: string | null; email: string };

/** How often the page asks whether Telegram confirmed the join. */
const CONFIRM_POLL_MS = 2500;

function LoginForm({ initialEmail, onMerged }: { initialEmail: string; onMerged: (m: Merged) => Promise<void> }) {
  const { t } = useTranslation();
  const id = useId();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [working, setWorking] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  // The latest callback, so a re-render of the section does not restart the wait.
  const merged = useRef(onMerged);
  merged.current = onMerged;

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);

  // WAITING FOR TELEGRAM: ask until the bot confirmed (the website session
  // comes back), refused, or the link expired. A failed read is retried.
  useEffect(() => {
    if (!pending) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const stop = (f: Failure | null) => {
      stopped = true;
      setPending(null);
      setWorking(false);
      if (f) setFailure(f);
    };
    const tick = async () => {
      if (stopped) return;
      if (Date.now() > pending.until) return stop({ key: "landingCredits.login.expired" });
      const r = await api.mergeStudioStatus(pending.poll).catch(() => null);
      if (stopped) return;
      if (r && r.success && r.data && r.data.status === "done" && r.data.token) {
        stopped = true;
        await merged.current({ email: r.data.user.email || pending.email, credits: Number(r.data.user.credits) || 0 });
        return;
      }
      if (r && !r.success && (r.status === 409 || r.status === 410)) return stop(mergeFailure(r));
      timer = setTimeout(() => void tick(), CONFIRM_POLL_MS);
    };
    timer = setTimeout(() => void tick(), CONFIRM_POLL_MS);
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [pending]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!address || !password) {
      setFailure({ key: "landingCredits.login.invalid" });
      return;
    }
    setWorking(true);
    setFailure(null);
    const r = await api.mergeStudio({ email: address, password });
    const data = r.success ? r.data : undefined;
    if (data && "token" in data && data.token) {
      await onMerged({ email: data.user.email || address, credits: Number(data.user.credits) || 0 });
      return;
    }
    if (data && "poll" in data && data.poll) {
      setPassword("");
      setPending({ url: data.confirm_url, poll: data.poll, email: address, username: data.telegram_username || null,
        until: Date.now() + (Number(data.expires_in_seconds) || 900) * 1000 });
      return;
    }
    setWorking(false);
    setFailure(mergeFailure(r));
  }

  if (pending) {
    return (
      <div data-testid="merge-confirm" className="mt-3 space-y-3">
        <p className="font-semibold text-foreground">{t("landingCredits.login.confirmTitle")}</p>
        <p className="text-muted-foreground">
          {pending.username
            ? t("landingCredits.login.confirmBodyUser", { username: pending.username, email: pending.email })
            : t("landingCredits.login.confirmBody", { email: pending.email })}
        </p>
        <a href={pending.url} target="_blank" rel="noopener noreferrer" data-testid="merge-confirm-open"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 font-bold text-primary-foreground hover:opacity-90">
          {t("landingCredits.login.confirmOpen")} <ExternalLink className="h-4 w-4" />
        </a>
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> {t("landingCredits.login.confirmWaiting")}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="login-form" className="mt-3 space-y-3">
      <p className="text-muted-foreground">{t("landingCredits.login.body")}</p>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-email`}>{t("landingCredits.email")}</Label>
        <Input id={`${id}-email`} type="email" autoComplete="email" value={email}
          onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-password`}>{t("landingCredits.password")}</Label>
        <Input id={`${id}-password`} type="password" autoComplete="current-password" value={password}
          onChange={(e) => setPassword(e.target.value)} />
      </div>
      <Button type="submit" className="w-full sm:w-auto" disabled={working}>
        {working ? t("landingCredits.login.working") : t("landingCredits.login.submit")}
      </Button>
      {failure && <p className="text-destructive">{t(failure.key, failure.values)}</p>}
    </form>
  );
}

/** Register / log in for a studio account, both folded away until asked for. */
function TelegramAccountForms({ onSaved, onMerged }: { onSaved: (email: string) => void; onMerged: (m: Merged) => Promise<void> }) {
  const { t } = useTranslation();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const trigger = "flex w-full items-center justify-between gap-2 rounded-md py-2 text-left font-semibold text-foreground hover:text-primary";
  const chevron = (open: boolean) => `h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`;

  return (
    <div data-testid="telegram-account" className="rounded-lg border border-border bg-card p-4 text-left text-sm">
      <p className="text-muted-foreground">{t("landingCredits.accountLead")}</p>
      <Collapsible open={registerOpen} onOpenChange={setRegisterOpen} className="mt-2 border-t border-border pt-2">
        <CollapsibleTrigger data-testid="register-toggle" className={trigger}>
          {t("landingCredits.register.toggle")}
          <ChevronDown className={chevron(registerOpen)} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          {/* An email that already has an account is theirs to sign in with: the form below opens with it. */}
          <RegisterForm onSaved={onSaved} onExists={(email) => { setLoginEmail(email); setLoginOpen(true); }} />
        </CollapsibleContent>
      </Collapsible>
      <Collapsible open={loginOpen} onOpenChange={setLoginOpen} className="mt-2 border-t border-border pt-2">
        <CollapsibleTrigger data-testid="login-toggle" className={trigger}>
          {t("landingCredits.login.toggle")}
          <ChevronDown className={chevron(loginOpen)} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <LoginForm initialEmail={loginEmail} onMerged={onMerged} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// THREE SIZES BY DEFAULT (Studio Blueprint 04): home shows `featured` (30,
// 120 and 500 credits) with a link to all eight on /pricing. A visit that
// came to buy (Telegram, a picked pack, a cancelled checkout) sees every
// pack, so the pack it asked for is always on screen. Prices never change
// here; shared/config/packs.js is the one price file.
export function CreditPacksSection({ featured }: { featured?: readonly string[] } = {}) {
  const { t } = useTranslation();
  const { refreshUser, user } = useAuth();
  const location = useLocation();
  const checkout = useCreditCheckout();
  const { telegram, visit, signin, packs, loading, loadError, buying, error, buy, locked } = checkout;
  const [saved, setSaved] = useState<string | null>(null);
  const [merged, setMerged] = useState<Merged | null>(null);
  const loggedIn = !!api.getToken();

  // The pack to mark: the one asked for, else (from the bot) the smallest
  // that covers what the customer was short of.
  const picked = checkout.wanted && packs.some((p) => p.type === checkout.wanted)
    ? checkout.wanted
    : checkout.needed ? packs.find((p) => p.points >= (checkout.needed as number))?.type ?? null : null;
  const pickBadge = telegram ? t("pricingPage.pickedForYou") : t("pricingPage.yourPick");
  const trimmed = !!featured && !picked && !isCreditsVisit(location.search, location.hash);
  const shown = trimmed ? packs.filter((p) => featured.includes(p.type)) : packs;

  // STRAIGHT TO THE PACKS, once they are in: the picked card, else the top of
  // the section. Index.tsx skips these visits (isCreditsVisit), so the two
  // never scroll against each other.
  const scrolled = useRef(false);
  const settle = useRef<(() => void) | null>(null);
  useEffect(() => () => settle.current?.(), []);
  useEffect(() => {
    if (loading || scrolled.current) return;
    if (!isCreditsVisit(location.search, location.hash)) return;
    scrolled.current = true;
    // Back / Forward: ScrollManager returns the page to where it was left.
    if (isHistoryRestore(location)) return;
    const target = picked && document.getElementById(`pack-${picked}`) ? `pack-${picked}` : CREDITS_ANCHOR;
    settle.current = scrollToSettled(target, target === CREDITS_ANCHOR ? "start" : "center");
  }, [loading, location, picked]);

  const onMerged = async (m: Merged) => {
    checkout.setAccount((prev) => ({ id: prev?.id || "", email: m.email, credits: m.credits, studio_account: false }));
    setMerged(m);
    await refreshUser();
  };

  const onSaved = (email: string) => {
    setSaved(email);
    void refreshUser();
  };

  // Signed in FROM TELEGRAM: only after a real exchange (useCreditCheckout).
  const signedIn = telegram && loggedIn && signin === "done";
  const telegramName = checkout.account && checkout.account.telegram_username;
  const showForms = signedIn && checkout.studioAccount && !saved && !merged;
  const balance = merged ? merged.credits : checkout.account ? checkout.account.credits : null;

  return (
    <section id={CREDITS_ANCHOR} data-testid="credits-section" className={`relative scroll-mt-24 ${featured ? "py-10 sm:py-16" : "py-16 sm:py-24"}`}>
      <div className="container mx-auto px-4">
        <div className={`text-center ${featured ? "mb-5 sm:mb-8" : "mb-8 sm:mb-12"}`}>
          <span className="mb-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary">
            <Coins className="h-4 w-4" /> {t("landingCredits.eyebrow")}
          </span>
          <h2 className="mb-3 text-3xl font-bold sm:text-4xl">{t("landingCredits.title")}</h2>
          <p className="mx-auto max-w-2xl text-muted-foreground">{t("landingCredits.lead")}</p>
        </div>

        {/* One plain line at a time about where the visit stands. */}
        <div className="mx-auto mb-8 max-w-2xl space-y-3 text-sm" aria-live="polite">
          {signin === "working" && (
            <div data-testid="credits-signing-in" className="flex items-center justify-center gap-2 rounded-lg border border-border bg-card p-4">
              <Loader2 className="h-4 w-4 animate-spin text-primary" /> {t("pricingPage.signingIn")}
            </div>
          )}
          {signin === "confirm" && (
            <div data-testid="credits-switch" className="rounded-lg border border-border bg-card p-4 text-center">
              <p className="mb-3 text-foreground">{t("landingCredits.switch.body", { email: user?.email || "" })}</p>
              <div className="flex flex-col justify-center gap-2 sm:flex-row">
                <Button type="button" data-testid="switch-telegram" onClick={checkout.continueWithTelegram}>
                  {t("landingCredits.switch.telegram")}
                </Button>
                <Button type="button" variant="outline" data-testid="switch-stay" onClick={checkout.stayAsWebsite}>
                  {t("landingCredits.switch.stay", { email: user?.email || "" })}
                </Button>
              </div>
            </div>
          )}
          {signedIn && !merged && (
            <div data-testid="credits-signed-in" className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-center">
              <p className="flex items-center justify-center gap-2"><Check className="h-4 w-4 shrink-0 text-primary" /> {telegramName
                ? t("landingCredits.signedInTelegramAs", { username: telegramName })
                : t("landingCredits.signedInTelegram")}</p>
              {balance !== null && (
                <p className="mt-1 text-muted-foreground">{t("landingCredits.balance", { credits: balance.toLocaleString("en-US") })}</p>
              )}
            </div>
          )}
          {merged && (
            <div data-testid="credits-merged" className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-center">
              <p className="flex items-center justify-center gap-2"><Check className="h-4 w-4 shrink-0 text-primary" /> {t("landingCredits.login.done", { email: merged.email })}</p>
              <p className="mt-1 text-muted-foreground">{t("landingCredits.balance", { credits: merged.credits.toLocaleString("en-US") })}</p>
            </div>
          )}
          {saved && (
            <div data-testid="credits-email-saved" className="rounded-lg border border-border bg-card p-4 text-center">
              {t("landingCredits.register.done", { email: saved })}
            </div>
          )}
          {showForms && <TelegramAccountForms onSaved={onSaved} onMerged={onMerged} />}
          {(signin === "failed" || signin === "unavailable") && (
            <div data-testid="credits-signin-failed" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-center">
              <p className="mb-3 text-foreground">
                {signin === "unavailable" ? t("landingCredits.signinUnavailable") : t("landingCredits.signinFailed")}
              </p>
              <a href={visit === "bot" ? "/go/bot" : "/go/studio"}
                className="inline-block rounded-lg bg-primary px-5 py-2 font-bold text-primary-foreground hover:opacity-90">
                {t("landingCredits.backToTelegram")}
              </a>
            </div>
          )}
          {checkout.cancelled && (
            <div data-testid="credits-checkout-cancelled" className="rounded-lg border border-border bg-card p-4 text-center text-foreground">
              {t("pricingPage.cancelled")}
            </div>
          )}
          {error !== null && (
            <div data-testid="credits-checkout-error" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-destructive">
              {t("pricingPage.checkoutFailedDetail", { reason: error || t("pricingPage.unknownError") })}
            </div>
          )}
        </div>

        {loading ? (
          <div data-testid="credits-loading" className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label={t("landingCredits.loading")} />
          </div>
        ) : loadError ? (
          <div data-testid="credits-load-error" className="mx-auto max-w-2xl rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-center text-sm">
            <p className="mb-3 text-foreground">{t("landingCredits.loadError")}</p>
            <Button type="button" variant="outline" size="sm" onClick={checkout.reload}>{t("landingCredits.retry")}</Button>
          </div>
        ) : (
          <>
            {trimmed ? (
              // Home: three slim rows, price on each Buy button (blueprint 04).
              <div className="mx-auto grid max-w-3xl gap-2.5 lg:max-w-5xl lg:grid-cols-3">
                {shown.map((p) => (
                  <PackRow key={p.type} pack={p} onBuy={(type) => void buy(type)} buying={buying} locked={locked} />
                ))}
              </div>
            ) : (
              <div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {shown.map((p) => (
                  <PackCard key={p.type} pack={p} highlight={p.type === picked}
                    badge={p.type === picked ? pickBadge : undefined}
                    subtitle={t("pricingPage.creditPack")} onBuy={(type) => void buy(type)} buying={buying} locked={locked} />
                ))}
              </div>
            )}
            <CheckoutStrip methods="packs" className="mt-6" />
            {trimmed && shown.length < packs.length && (
              <p className="mt-4 text-center">
                <Link to="/pricing" data-testid="credits-all-sizes" className="inline-flex min-h-[44px] items-center text-[15px] font-semibold text-primary underline-offset-4 hover:underline">
                  {t("home.allSizes", { count: packs.length })}
                </Link>
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
